import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Typography,
  Chip,
  Box,
  CircularProgress
} from '@mui/material';
import { Person } from '@mui/icons-material';
import { conversationsAPI } from '../../services/api';

interface Agent {
  id: number;
  name: string;
  email: string;
  avatar_url?: string;
}

interface AssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  conversationId: number;
  currentAssignee?: {
    id: number;
    full_name: string;
  };
  onAssigned: () => void;
}

const AssignmentDialog: React.FC<AssignmentDialogProps> = ({
  open,
  onClose,
  conversationId,
  currentAssignee,
  onAssigned
}) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (open) {
      loadAvailableAgents();
    }
  }, [open, conversationId]);

  const loadAvailableAgents = async () => {
    try {
      setLoading(true);
      const response = await conversationsAPI.getAvailableAgents(conversationId);
      setAgents(response.agents || []);
    } catch (error) {
      console.error('Failed to load agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (agentId?: number) => {
    try {
      setAssigning(true);
      await conversationsAPI.assignConversation(conversationId, agentId);
      onAssigned();
      onClose();
    } catch (error) {
      console.error('Failed to assign conversation:', error);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxHeight: '85vh' } }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Person />
          Assign Conversation
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose an agent to assign this conversation to:
            </Typography>
            
            {/* Current assignee */}
            {currentAssignee && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Currently assigned to:
                </Typography>
                <Chip
                  avatar={<Avatar sx={{ width: 24, height: 24 }}>{currentAssignee.full_name.charAt(0)}</Avatar>}
                  label={currentAssignee.full_name}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}
            
            <List>
              {/* Unassign option */}
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => handleAssign()}
                  disabled={assigning}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'grey.300' }}>
                      <Person />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Unassigned"
                    secondary="Remove current assignment"
                  />
                </ListItemButton>
              </ListItem>
              
              {/* Agent options */}
              {agents.map((agent) => (
                <ListItem key={agent.id} disablePadding>
                  <ListItemButton 
                    onClick={() => handleAssign(agent.id)}
                    disabled={assigning || agent.id === currentAssignee?.id}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={agent.avatar_url}
                        sx={{ bgcolor: 'primary.main' }}
                      >
                        {agent.name.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={agent.name}
                      secondary={agent.email}
                    />
                    {agent.id === currentAssignee?.id && (
                      <Chip label="Current" size="small" color="primary" />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={assigning}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignmentDialog;